/**
 * Definition list containing  key-value pairs.
 *
 * @module views/components/MetadataList
 * @memberof -Common
 */
import './MetadataList.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Text from '../Text/Text';

export const LineItem = ( {
  label,
  labelColor,
  labelTextStyle,
  stacked,
  value,
  valueColor,
  valueTextStyle
} ) => {
  if( !label || !value ){
    return null;
  }
  return (
    <div
      className={
        classNames( 'MetadataList__LineItem', {
          'MetadataList__LineItem--stacked': !!stacked
        } )
      }
    >
      <dt>
        <Text
          color={ labelColor }
          htmlTag='span'
          textStyle={ labelTextStyle }
        >
          { label }
        </Text>
      </dt>
      <dd>
        <Text
          color={ valueColor }
          htmlTag='span'
          textStyle={ valueTextStyle }
        >
          { value }
        </Text>
      </dd>
    </div>
  );
};

/**
 * Represents a MetadataList component
 *
 * @method
 * @param { MetadataListProps} props - React properties passed from composition
 * @returns MetadataList
 */
export const MetadataList = function( props ){
  const {
    containerClassName = defaultProps.containerClassName,
    componentKey,
    data = defaultProps.data,
    labelColor,
    labelTextStyle,
    stacked = defaultProps.stacked,
    title = defaultProps.title,
    valueColor,
    valueTextStyle
  } = props;

  if( !title ){
    // eslint-disable-next-line no-console
    console.error( 'The prop `title` is marked as required in `MetadataList`, but its value is `undefined`.' );
    return null;
  }

  if( !data.length ){
    return null;
  }

  return (
    <dl
      className={ classNames( 'MetadataList', {
        [containerClassName]: !!containerClassName
      } ) }
      title={ title }
    >
      { data.map( ( item, idx ) => (
        <LineItem
          key={ `${componentKey}-${item[0]}-${item[1]}-${idx}` }
          label={ item[0] }
          labelColor={ labelColor }
          labelTextStyle={ labelTextStyle }
          stacked={ stacked }
          value={ item[1] }
          valueColor={ valueColor }
          valueTextStyle={ valueTextStyle }
        />
      ) ) }
    </dl>
  );
};

/**
 * Property type definitions
 * @typedef MetadataListProps
 * @type {object}
 * @property {string} containerClassName - container class used for positioning list items
 * @property {string} componentKey - String representing the component's key
 * @property {Array.<Array.<string>>} data - array of key value pairs ex/ [[key, value], [key, value]]
 * @property {string} labelColor - Text color for the value Text component
 * @property {string} labelTextStyle - Text style for the value Text component
 * @property {boolean} stacked - stacks label on top of value when displayed
 * @property {string} title - title used for ADA purposes for the entire list
 * @property {string} valueColor - Text color for the value Text component
 * @property {string} valueTextStyle - Text style for the value Text component
 */
export const propTypes =  {
  containerClassName: PropTypes.string,
  componentKey: PropTypes.string,
  data: PropTypes.arrayOf( PropTypes.arrayOf( PropTypes.oneOfType( [PropTypes.string, PropTypes.node] ) ) ).isRequired,
  labelColor: PropTypes.string,
  labelTextStyle: PropTypes.string,
  stacked: PropTypes.bool,
  title: PropTypes.string.isRequired,
  valueColor: PropTypes.string,
  valueTextStyle: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} containerClassName=''
 */
export const defaultProps =  {
  containerClassName: '',
  data: [],
  stacked: false,
  title: undefined
};

MetadataList.propTypes = propTypes;
MetadataList.defaultProps = defaultProps;

export default MetadataList;
